import { LitElement, html, css } from 'lit';
import { createRef, ref as elRef } from 'lit/directives/ref.js';
import { choose } from 'lit/directives/choose.js';
import { KatapultDialog } from '../katapult-elements/katapult-dialog.js';
import { getTransferLocking } from '../../modules/TransferLocking.js';
import '../katapult-elements/katapult-button.js';

// Only allow one dialog of each type to be open at a time.
let dialogOpen = false;

/**
 * Tests whether or not a job is locked.
 * @param {string} jobId - The ID of the job to check.
 * @param {string} [jobOwner] - The owner of the job. If not provided, it will be fetched from the database.
 * @returns {Promise<boolean>} Whether or not the job is locked.
 */
export async function isJobLocked(jobId, jobOwner) {
  // If the job ID is not provided, it is not locked.
  if (!jobId) return false;

  // If the job owner is not provided, fetch it from the database.
  if (!jobOwner) {
    jobOwner = (await globalThis.FirebaseWorker.database().ref(`photoheight/jobs/${jobId}/job_owner`).once('value'))?.val();
  }

  // Get the transfer locking information for the job owner to see if jobs are locked.
  const jobIsLocked = Boolean((await getTransferLocking(jobOwner ?? ''))?.lock_jobs);

  return jobIsLocked;
}

/**
 * Shows a dialog indicating that the job is locked.
 * @param {string} jobId - The ID of the job to show the dialog for.
 */
export async function showLockedJobDialog(jobId) {
  if (dialogOpen) return;
  dialogOpen = true;

  // Create a manual transfer dialog and add it to the DOM.
  const dialog = new ManualTransferDialog(jobId);
  document.body.appendChild(dialog);
  await dialog.updateComplete;

  // Open the dialog.
  dialog.open();
  // On close, remove the dialog from the DOM.
  dialog.dialogRef.value?.closeComplete.then(() => {
    document.body.removeChild(dialog);
    dialogOpen = false;
  });
}

class ManualTransferDialog extends LitElement {
  static styles = css`
    .flex-column {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }
    .button-container {
      width: 100%;
      display: flex;
      justify-content: space-between;
    }
    paper-spinner-lite {
      width: 50px;
      height: 50px;
      position: fixed;
      --paper-spinner-color: var(--secondary-color);
    }
  `;

  static properties = {
    dialogState: { type: String, state: true },
    jobId: { type: String },
    jobName: { type: String },
    jobOwner: { type: String },
    errorMessage: { type: String },
    fromServerId: { type: String },
    fromDomain: { type: String },
    toServerId: { type: String },
    toDomain: { type: String }
  };

  constructor(jobId) {
    super();

    this.dialogState = 'checking-job';
    this.jobId = jobId || '';
    this.jobName = '';
    this.jobOwner = '';
    this.errorMessage = '';
    this.fromServerId = '';
    this.fromDomain = '';
    this.toServerId = '';
    this.toDomain = '';
  }

  /**
   * Internal reference to the dialog element.
   * @type {import('lit/directives/ref.js').Ref<KatapultDialog>}
   */
  dialogRef = createRef();

  /** Colors to use for the dialog */
  stateConfigs = {
    'checking-job': {
      title: 'Checking Job',
      color: 'var(--secondary-color)',
      icon: 'pending'
    },
    'job-locked-notice': {
      title: 'Job Locked',
      color: 'var(--secondary-color)',
      icon: 'lock'
    },
    'transfer-notice': {
      title: 'Manual Transfer',
      color: 'var(--secondary-color)',
      icon: 'move_item'
    },
    'in-progress': {
      title: 'Manual Transfer',
      color: 'var(--secondary-color)',
      icon: 'move_item'
    },
    'success-notification': {
      title: 'Transfer Complete',
      color: 'var(--paper-green-500)',
      icon: 'check_circle'
    },
    'completed-notification': {
      title: 'Job Transferred',
      color: 'var(--secondary-color)',
      icon: 'check_circle'
    },
    'error-notification': {
      title: 'Transfer Failed',
      color: 'var(--error-color)',
      icon: 'error'
    }
  };

  render() {
    return html`
      <katapult-dialog ${elRef(this.dialogRef)} modal closeButton .config="${this.stateConfigs[this.dialogState]}" .width="${500}">
        ${choose(this.dialogState, [
          [
            'checking-job',
            () => html`
              <div class="flex-column" style="align-items: center; gap: 32px;">
                <div style="font-weight: bold;">Checking if job is locked...</div>
                <div style="width: 50px; height: 50px;">
                  <paper-spinner-lite active></paper-spinner-lite>
                </div>
              </div>
            `
          ],
          [
            'job-locked-notice',
            () => html`
              <div class="flex-column">
                <div>
                  ${this.jobName ? html`<b>${this.jobName}</b>` : 'This job'} is locked on
                  ${this.fromDomain ? html`<b>${this.fromDomain}</b>` : 'this server'}.
                </div>
                <div>It is scheduled to be transferred to another server, and may not be viewed, edited, or modified in any way.</div>
                <div>
                  If you need to access this job, you can transfer it manually to
                  ${this.toDomain ? html`<b>${this.toDomain}</b>` : 'the private server'}.
                </div>
              </div>
              <div slot="buttons" class="button-container">
                <katapult-button outlined dialog-dismiss>Close</katapult-button>
                <katapult-button
                  color="var(--secondary-color)"
                  style="min-width: 120px;"
                  @click="${() => (this.dialogState = 'transfer-notice')}"
                  >Transfer Now</katapult-button
                >
              </div>
            `
          ],
          [
            'transfer-notice',
            () => html`
              <div class="flex-column">
                <div>Are you sure you want to transfer ${this.jobName ? html`<b>${this.jobName}</b>` : 'this job'} now?</div>
                <div>
                  This will transfer the job from ${this.fromDomain ? html`<b>${this.fromDomain}</b>` : 'this server'} to
                  ${this.toDomain ? html`<b>${this.toDomain}</b>` : 'a private server'}.
                </div>
                <div>Once the transfer is started it cannot be canceled; it may take several minutes to complete.</div>
              </div>
              <div slot="buttons" class="button-container">
                <katapult-button outlined style="width: 120px;" dialog-dismiss>Cancel</katapult-button>
                <katapult-button color="var(--secondary-color)" style="min-width: 120px;" @click="${() => this.handleTransferClicked()}"
                  >Confirm and Start Transfer</katapult-button
                >
              </div>
            `
          ],
          [
            'in-progress',
            () => html`
              <div class="flex-column" style="align-items: center; gap: 32px;">
                <div style="font-weight: bold;">Transferring ${this.jobName ? html`<b>${this.jobName}</b>` : 'job'}</div>
                <div style="width: 50px; height: 50px;">
                  <paper-spinner-lite active></paper-spinner-lite>
                </div>
                <div>This may take several minutes.</div>
              </div>
            `
          ],
          [
            'success-notification',
            () => html`
              <div class="flex-column">
                <div>${this.jobName ? html`<b>${this.jobName}</b>` : 'The job'} transferred successfully!</div>
                <div>It is now available on ${this.toDomain ? html`<b>${this.toDomain}</b>` : 'the private server'}.</div>
              </div>
              <div slot="buttons" class="button-container">
                <katapult-button outlined dialog-dismiss>Close</katapult-button>
                <katapult-button
                  icon="open_in_new"
                  color="var(--paper-green-500)"
                  style="min-width: 120px;"
                  @click="${() => this.openTransferredJob()}"
                  >View Job</katapult-button
                >
              </div>
            `
          ],
          [
            'completed-notification',
            () => html`
              <div class="flex-column">
                <div>
                  ${this.jobName ? html`<b>${this.jobName}</b>` : 'The job'} has been transferred to
                  ${this.toDomain ? html`<b>${this.toDomain}</b>` : 'the private server'}.
                </div>
              </div>
              <div slot="buttons" class="button-container">
                <katapult-button outlined dialog-dismiss>Close</katapult-button>
                <katapult-button
                  icon="open_in_new"
                  color="var(--secondary-color)"
                  style="min-width: 120px;"
                  @click="${() => this.openTransferredJob()}"
                  >View Job</katapult-button
                >
              </div>
            `
          ],
          [
            'error-notification',
            () => html`
              <div class="flex-column">
                <div>Something went wrong while trying to transfer the job.</div>
                <div>Please try again later, or contact support if the problem persists.</div>
                <div>Error: ${this.errorMessage || 'Unknown error'}</div>
              </div>
            `
          ]
        ])}
      </katapult-dialog>
    `;
  }

  async open() {
    // Open the dialog.
    this.dialogRef.value?.open();

    // Get transfer operation information.
    const jobRef = globalThis.FirebaseWorker.database().ref(`photoheight/jobs/${this.jobId}`);
    this.jobName = (await jobRef.child(`name`).once('value'))?.val();
    this.jobOwner = (await jobRef.child(`job_owner`).once('value'))?.val();
    const transferLocking = await getTransferLocking(this.jobOwner);
    this.fromServerId = transferLocking?.from_server_id ?? '';
    this.fromDomain = transferLocking?.from_domain ?? '';
    this.toServerId = transferLocking?.to_server_id ?? '';
    this.toDomain = transferLocking?.to_domain ?? '';
    const transferOperationRef = jobRef.child(`transfer_operation`);
    const transferOperation = (await transferOperationRef.once('value'))?.val();

    // Set the dialog state based on the transfer operation.
    if (transferOperation?.transfer_status == 'COMPLETED') this.dialogState = 'completed-notification';
    // If the transfer operation is in progress, listen for changes to the transfer operation.
    else if (transferOperation?.transfer_status == 'IN_PROGRESS') {
      this.dialogState = 'in-progress';

      // Listen for changes to the transfer operation.
      transferOperationRef.on('value', (snapshot) => {
        const transferOperation = snapshot.val();
        if (transferOperation?.transfer_status == 'COMPLETED') {
          this.dialogState = 'success-notification';
        } else if (transferOperation?.transfer_status == 'FAILED') {
          this.dialogState = 'error-notification';
          this.errorMessage = transferOperation?.transfer_error;
        }
      });

      // When the dialog is closed, remove the listener.
      this.dialogRef.value?.closeComplete.then(() => {
        console.log('Removing listener');
        transferOperationRef.off();
      });
    }
    // Otherwise, show the locked job notice.
    else this.dialogState = 'job-locked-notice';
  }

  close() {
    this.dialogRef.value?.close();
  }

  async handleTransferClicked() {
    // Hide the transfer notice and un-slot the buttons
    this.dialogState = 'in-progress';

    try {
      // Call the transfer function and await the result
      await this.callManuallyTransferCompanyJob();
      // If successful, show the success message
      this.dialogState = 'success-notification';
    } catch (error) {
      // If unsuccessful, show the error message
      this.dialogState = 'error-notification';
      this.errorMessage = error.message;
      console.error(error);
    }
  }

  /** Calls the manual transfer function */
  async callManuallyTransferCompanyJob() {
    // Specify the function endpoint.
    const endpoint = 'https://us-central1-katapult-pro-shared-resources.cloudfunctions.net/manuallyTransferCompanyJob';
    // Set request headers.
    const headers = { 'Content-Type': 'application/json' };
    // Build request body.
    const body = {
      jobId: this.jobId,
      companyId: this.jobOwner,
      serverId: this.fromServerId,
      toProjectId: this.toServerId,
      /** @ts-ignore */
      jwt: await firebase.auth().currentUser?.getIdToken(true)
    };

    // Perform the request.
    const res = await fetch(endpoint, { method: 'POST', headers, body: JSON.stringify(body) });

    // Throw an error if the request was not successful.
    if (!res.ok) throw new Error(`Failed to transfer job ${this.jobId}`);
  }

  async openTransferredJob() {
    const targetURL = new URL(window.location.href);
    const siteVersion = targetURL.host.split('.').find((x) => ['alpha', 'beta', 'legacy'].includes(x));
    targetURL.port = '';
    targetURL.hostname = `${siteVersion ? `${siteVersion}.` : ''}${this.toDomain}`;
    targetURL.hash = `#${this.jobId}`;
    window.open(targetURL);
  }
}

window.customElements.define('manual-transfer-dialog', ManualTransferDialog);
